import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  ChangeDetectorRef,
  Component,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  input,
  inject,
  DestroyRef,
  viewChild,
} from '@angular/core'

import { TranslateService, TranslatePipe } from '@ngx-translate/core'

import { FormMode } from '@core/form/form.enums'
import { ToastService } from '@core/components/toast/toast.service'
import {
  formModeFn,
  onCloseCancelClick,
  onConfirmClose,
  onOverlayClose,
  save,
  toggleEdit,
} from '@core/form/form.utils'

import { OverlayType } from '../overlay/overlay.enums'
import { Overlay } from '../overlay/overlay'
import { GeneralForm } from '../general-form/general-form'
import { UserProfile } from '@core/authentication/auth/auth.models'
import { AuthPage, RoleType } from '@core/authentication/auth/auth.enums'
import { APPLICATION_DATA } from '@shared/components/quick-view/constants/quick-view.constants'
import { ProfileStore } from '@core/store/profile/profile.store'
import { MenuItem } from '@shared/modules/primeng-clone/api'
import { Menu } from '@shared/modules/primeng-clone/menu'
import { Button } from '../button/button'
import { AsyncPipe } from '@angular/common'
import { RoleTranslatePipe } from '@shared/pipes/role-translate.pipe'

interface TranslateMenuItem extends MenuItem {
  translateLabel?: string
  activate?: () => boolean
}

@Component({
  selector: 'bo-quick-view',
  templateUrl: './quick-view.html',
  styleUrls: ['./quick-view.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Menu, Overlay, GeneralForm, Button, AsyncPipe, TranslatePipe, RoleTranslatePipe],
})
export class QuickView implements OnChanges {
  readonly #destroyRef = inject(DestroyRef)
  private ref = inject(ChangeDetectorRef)
  readonly #profileStore = inject(ProfileStore)
  private toastService = inject(ToastService)
  readonly #translatorService = inject(TranslateService)

  private _formMode = FormMode.Read
  private userMenuItemsConfig: TranslateMenuItem[] = [
    {
      translateLabel: 'MTR_TITLE_ADMIN',
      routerLink: AuthPage.Administration,
      icon: 'u-icon u-icon--lg u-icon--profile',
      activate: () =>
        ((this.userProfile() || ({} as UserProfile)).roles || []).includes(RoleType.Admin),
    },
    {
      translateLabel: 'MTR_COMMON_PROFILE',
      icon: 'u-icon u-icon--lg u-icon--profile',
      command: () => {
        this.overlay().open()
      },
      activate: () => ((this.userProfile() || ({} as UserProfile)).roles || []).length > 0,
    },
    {
      translateLabel: 'MTR_COMMON_LOGOUT',
      routerLink: 'logout',
      icon: 'u-icon u-icon--lg u-icon--logout',
    },
  ]

  readonly overlay = viewChild<Overlay>('overlay')
  readonly profile = viewChild<GeneralForm>('profile')
  readonly userProfile = input.required<UserProfile>()

  formChangesObj!: UserProfile
  OverlayType = OverlayType
  defaultAvatar = '../../../../../assets/images/user-default-avatar.svg'
  userMenuItems!: MenuItem[]

  readonly appData = APPLICATION_DATA

  get formMode() {
    return this._formMode
  }
  set formMode(_formMode) {
    formModeFn(this, _formMode)
  }

  constructor() {
    this.#translatorService
      .stream(this.userMenuItemsConfig.map(({ translateLabel }) => translateLabel))
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((translatedLabels) =>
        this.userMenuItemsConfig.forEach(
          (menuItem) => (menuItem.label = translatedLabels[menuItem.translateLabel]),
        ),
      )
  }

  ngOnChanges(changes: SimpleChanges) {
    const userProfileChanges = (changes['userProfile'] || ({} as SimpleChanges)).currentValue
    if (userProfileChanges) {
      this.userMenuItems = this.userMenuItemsConfig.filter(
        (userMenuItem) => !userMenuItem.activate || userMenuItem.activate(),
      )
    }
  }

  onConfirmClose(isCloseConfirmed) {
    onConfirmClose(this, isCloseConfirmed)
  }

  onOverlayClose() {
    // resetForm() resets with emitEvent: false, so no onChange emission clears this:
    // a stale formChangesObj would keep the unsaved-changes dialog and the enabled
    // Save button armed on the next open with no actual changes
    this.formChangesObj = null
    onOverlayClose(this)
    this.profile().resetForm()
  }

  onUserProfileChange(userProfile: UserProfile) {
    this.formChangesObj = userProfile
  }

  toggleEdit(formMode?: FormMode, formChangesObj = {}) {
    toggleEdit(this, formMode, formChangesObj)
  }

  onCloseCancelClick() {
    onCloseCancelClick(this)
  }

  onSaveEditClick() {
    this.save()
  }

  save() {
    const save$ = this.#profileStore.saveUserProfile({
      profile: { ...this.profile().userForm.getRawValue(), roles: [] },
    })
    save(this, save$, 'MTR_SUCCESS_EDIT_USER_PROFILE')
  }
}
